import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Table, 
  Input, 
  Button, 
  message,
  Typography,
  Avatar,
  Tag
} from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined } from '@ant-design/icons';
import PageLayout from '../../../components/layout/PageLayout';
import masterService from '../../../services/masterService';
import { useUser } from '../../../context/UserContext';

const { Title, Text } = Typography;
const { Search } = Input;

const CurrencyList = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} currencies`
  });
  
  // Filters
  const [filters, setFilters] = useState({
    searchText: ''
  });

  const getCurrencies = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      
      const requestParams = {
        perPage: pagination.pageSize,
        currentPage: pagination.current,
        searchText: filters.searchText,
        ...params
      };
      
      console.log('Fetching currencies with params:', requestParams);
      const response = await masterService.getCurrencies(requestParams);
      console.log('Currencies API Response:', response);
      
      if (response.data && response.data.status) {
        // Handle different possible response structures
        const currenciesData = response.data.data?.data || response.data.data || [];
        const paginationData = response.data.data?.pagination || response.data.pagination || {};
        
        console.log('Currencies data:', currenciesData);
        console.log('Pagination data:', paginationData);
        
        // Ensure currenciesData is an array
        if (Array.isArray(currenciesData)) {
          setCurrencies(currenciesData);
        } else {
          console.error('Currencies data is not an array:', currenciesData);
          setCurrencies([]);
        }
        
        setPagination(prev => ({
          ...prev,
          ...paginationData,
          current: paginationData.currentPage || 1
        }));
      } else {
        message.error('Failed to fetch currencies');
        setCurrencies([]);
      }
    } catch (error) {
      console.error('Error fetching currencies:', error);
      message.error('Failed to fetch currencies');
      setCurrencies([]);
    } finally {
      setLoading(false);
    }
  }, [filters.searchText]);

  // Load currencies data
  useEffect(() => {
    getCurrencies();
  }, [getCurrencies]);

  // Handle search
  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, searchText: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
    getCurrencies({
      searchText: value,
      perPage: pagination.pageSize,
      currentPage: 1
    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;
    
    setPagination(prev => ({
      ...prev,
      current,
      pageSize
    }));
    
    getCurrencies({
      searchText: filters.searchText,
      perPage: pageSize,
      currentPage: current
    });
  };

  // Handle add currency
  const handleAddCurrency = () => {
    navigate('/master/addcurrency');
  };

  // Handle edit currency
  const handleEditCurrency = (currId) => {
    navigate(`/master/editcurrency/${currId}`);
  };

  // Table columns
  const columns = [
    {
      title: 'Currency Name',
      dataIndex: 'curr_name',
      key: 'curr_name',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.curr_name.localeCompare(b.curr_name),
      render: (text) => (
        <Text strong style={{ fontSize: '14px' }}>
          {text}
        </Text>
      )
    },
    {
      title: 'Currency Code',
      dataIndex: 'curr_code',
      key: 'curr_code',
      width: 150,
      render: (text) => (
        <Tag color="blue" style={{ fontSize: '12px' }}>
          {text}
        </Tag>
      )
    },
    {
      title: 'Status',
      dataIndex: 'curr_is_active',
      key: 'curr_is_active',
      width: 120,
      render: (status) => (
        <Tag color={status === 'Y' ? 'green' : 'red'}>
          {status === 'Y' ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditCurrency(record.curr_id)}
          size="small"
        >
          Edit
        </Button>
      )
    }
  ];

  // Header content with user info
  const headerContent = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Avatar 
          src={user?.avatar} 
          size={64} 
          style={{ marginRight: '16px' }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <div>
          <Title level={4} style={{ margin: 0, marginBottom: '4px' }}>
            {user?.name || 'Admin'}
          </Title>
          <Text type="secondary">Manage Currencies</Text>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="Currency List"
      desc="Manage currencies and their status"
      avatar={user?.avatar}
      headerContent={headerContent}
    >
      <Card>
        <div style={{ marginBottom: '16px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <Title level={4} style={{ margin: 0 }}>
              Currencies ({pagination.total})
            </Title>
            
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddCurrency}
              size="large"
            >
              Add Currency
            </Button>
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '16px', 
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <Search
              placeholder="Search currencies..."
              allowClear
              onSearch={handleSearch}
              style={{ width: '50%', minWidth: '200px' }}
              enterButton={<SearchOutlined />}
            />
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={currencies}
          rowKey="curr_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          size="middle"
          locale={{ 
            emptyText: 'No currencies found',
            filterConfirm: 'OK',
            filterReset: 'Reset',
            filterEmptyText: 'No filters'
          }}
        />
      </Card>
    </PageLayout>
  );
};

export default CurrencyList;